import { InputWrapper } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { MouseEvent, useState } from 'react';
import AceEditor from 'react-ace';

import 'ace-builds/src-noconflict/ext-language_tools';
import 'ace-builds/src-noconflict/mode-handlebars';
import 'ace-builds/src-noconflict/mode-java';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/mode-html';
import 'ace-builds/src-noconflict/theme-tomorrow';
import styles from './inputTemplate.module.scss';
import type {
  EditorMode,
  EditorOptions,
  InputTemplateProps,
} from './inputTemplate.types';

const defaultEditorMode: EditorMode = 'handlebars';
const defaultEditorOptions: EditorOptions = {
  tabSize: 2,
  minLines: 10,
  maxLines: 10,
  fontSize: 14,
  enableBasicAutocompletion: true,
  enableLiveAutocompletion: true,
  showPrintMargin: false,
  showGutter: true,
  readOnly: false,
};

/**
 * A component that wraps the AceEditor furthermore with input wrapper from
 * habla which adds all validation and input states automatically.
 */
export const InputTemplate = ({
  height,
  readOnly,
  disabled,
  active,
  defaultHidden,
  previewContent,
  previewButton,
  editorClassName,
  editorOptions,
  fieldTemplateId,
  status,
  prefix,
  suffix,
  icon,
  placeholder,
  mode = defaultEditorMode,
  ...editorProps
}: InputTemplateProps): JSX.Element => {
  editorOptions = {
    ...defaultEditorOptions,
    ...editorOptions,
    ...((disabled || readOnly) && { readOnly: true }),
  };
  const [showTemplateContent, setShowTemplateContent] = useState<boolean>(
    defaultHidden ? false : true,
  );

  return (
    <InputWrapper
      className={classnames(styles.inputWrapper, {
        [styles.inputWrapperWithPreview]: !!previewContent,
      })}
      status={status}
      active={active}
      prefix={prefix}
      suffix={suffix}
      icon={icon}
    >
      {previewButton && (
        <div role="button" className={styles.previewButton}>
          {previewButton}
        </div>
      )}
      {previewContent ||
        (showTemplateContent ? (
          <AceEditor
            theme="tomorrow"
            width="100%"
            height={height}
            name={fieldTemplateId}
            mode={mode}
            scrollMargin={[5, 5]}
            setOptions={{
              ...editorOptions,
              ...(disabled && {
                highlightGutterLine: false,
                highlightActiveLine: false,
              }),
              // fix to prevent "Could not load worker" error when changing `mode`
              useWorker: false,
            }}
            className={classnames(styles.editor, editorClassName, {
              [styles.editorDisabled]: disabled,
              [styles.editorGreyedOut]: readOnly,
            })}
            placeholder={placeholder}
            value={editorProps.value}
            onChange={editorProps.onChange}
            onFocus={editorProps?.onFocus}
          />
        ) : (
          <div className={styles.templateCondition}>
            If you want to customize this template click{' '}
            <a
              href="#"
              onClick={(event: MouseEvent<HTMLElement>) => {
                event.preventDefault();
                setShowTemplateContent(!showTemplateContent);
              }}
            >
              here
            </a>
          </div>
        ))}
    </InputWrapper>
  );
};
